import type { HttpValidationError } from "@common/client"
import { AxiosError } from "axios"

/**
 * Minimal error handling utility.
 *
 * Backend errors are already sanitized (CWE-209 compliance), so the frontend
 * only needs to handle:
 * 1. Network errors (connection issues, timeouts)
 * 2. Status code fallbacks (when backend doesn't provide detail)
 * 3. Pass through sanitized backend messages
 */

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again."

/**
 * Type guard for ApiError from the generated client
 */
function isHttpError(error: unknown): error is HttpValidationError {
  return typeof error === "object" && error !== null && "detail" in error
}

/**
 * Returns a user-friendly message based on HTTP status code.
 * Used as fallback when backend doesn't provide a detail message.
 */
function getStatusMessage(status: number, fallback: string): string {
  switch (status) {
    case 400:
      return "Invalid request. Please check your input and try again."
    case 401:
      return "Your session has expired. Please log in again."
    case 403:
      return "You don't have permission to perform this action."
    case 404:
      return "The requested resource was not found."
    case 409:
      return "This action conflicts with existing data. Please refresh and try again."
    case 422:
      return "Please check your input and try again."
    case 429:
      return "Too many requests. Please wait a moment and try again."
    case 500:
      return "Something went wrong on our end. Please try again later."
    case 502:
      return "We're experiencing connection issues. Please try again later."
    case 503:
      return "The service is temporarily unavailable. Please try again later."
    case 504:
      return "The request took too long. Please try again."
    default:
      return fallback
  }
}

/**
 * Extracts a user-friendly error message from various error types.
 *
 * Backend messages are already sanitized, so we pass them through directly.
 * Only network errors and missing details need client-side handling.
 *
 * @param error - The error object (ApiError, AxiosError, or generic Error)
 * @param fallback - Optional custom fallback message for this context
 * @returns A user-friendly error message
 *
 * NB the types are wrong here, mutations and queries throw AxiosErrors
 * hence the runtime checks, this can be fixed to work at compile time - but later
 */
export function getErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE,
): string {
  if (!error) {
    return fallback
  }

  // Handle ApiError from generated client
  if (
    error instanceof AxiosError &&
    "error" in error &&
    isHttpError(error.error)
  ) {
    if (error?.error.detail) {
      // Handle array of validation errors (FastAPI format)
      if (Array.isArray(error.error.detail) && error.error.detail.length > 0) {
        const firstError = error.error.detail[0]
        if (typeof firstError === "object" && firstError?.msg) {
          return String(firstError.msg)
        }
        if (typeof firstError === "string") {
          return firstError
        }
      }

      // Handle string detail - already sanitized by backend
      if (typeof error.error.detail === "string") {
        return error.error.detail
      }
    }

    // Fall back to status-based message
    return getStatusMessage(error.status ?? 500, fallback)
  }

  // Handle AxiosError (network issues, timeouts)
  if (error instanceof AxiosError) {
    console.log(error.code)
    // Network error - no connection to server
    if (error.code === "ERR_NETWORK") {
      return "Unable to connect to the server. Please check your internet connection."
    }

    // Timeout
    if (error.code === "ECONNABORTED" || error.code === "ERR_CANCELED") {
      return "The request took too long. Please try again."
    }

    // Try response detail - already sanitized by backend
    const responseDetail = error.response?.data?.detail
    if (responseDetail && typeof responseDetail === "string") {
      return responseDetail
    }

    // Use status-based message
    if (error.response?.status) {
      return getStatusMessage(error.response.status, fallback)
    }

    return fallback
  }

  // Handle generic Error (rare, but possible)
  if (error instanceof Error) {
    // Network-related error messages
    if (
      error.message.includes("ERR_NETWORK") ||
      error.message.includes("fetch")
    ) {
      return "Unable to connect to the server. Please check your internet connection."
    }
    // For other errors, use fallback to avoid exposing internal details
    return fallback
  }

  return fallback
}

export function isSessionNotFreshError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === 403 &&
    error.response.headers["x-error-code"] === "SESSION_NOT_FRESH"
  )
}

export { DEFAULT_ERROR_MESSAGE }
